import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ApiResponseCheckPublicServiceConsentResponse, CreatePublicServiceConsentRequest} from '@di-shared/api/di/__generated';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import DemeritEducationContents from '@di-shared/features/public-service/demerit-education/DemeritEducationContents';
import HighwayUnpaidContents from '@di-shared/features/public-service/highway-unpaid/HighwayUnpaidContents';
import LicenseRenewalContents from '@di-shared/features/public-service/license-renewal/LicenseRenewalContents';
import VehicleInspectionContents from '@di-shared/features/public-service/vehicle-inspection/VehicleInspectionContents';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface PublicServiceContentsProps {
  vin: string;
}

function PublicServiceContents(props: PublicServiceContentsProps) {
  return (
    <Suspense fallback={<PublicServiceContentsLoading />}>
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({vin}: PublicServiceContentsProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const puid = useEnvironmentStore((state) => state.puid);

  const {data: publicServiceContentsData} = useSuspenseQuery({
    queryKey: [
      getQueryKey(diApi.publicService.checkPublicServiceConsents),
      puid,
      Object.values(CreatePublicServiceConsentRequest.category),
    ],
    queryFn: () =>
      diApi.publicService.checkPublicServiceConsents({
        puid,
        categoryList: Object.values(CreatePublicServiceConsentRequest.category),
      }),
    select: ({data}: ApiResponseCheckPublicServiceConsentResponse) => {
      return {
        ...data,
        consents: data.consents.sort((a, b) => Number(b.status) - Number(a.status)),
      };
    },
    refetchOnWindowFocus: true,
  });

  function goToPublicServiceTerms() {
    navigate(ROUTE_PATHS.PUBLIC_SERVICE_CONSENTS.getFullPath(vin));
  }

  return (
    <section className="section section_application">
      <span className="logo">
        <span
          className="ic ic_digital_service"
          role="img"
          aria-label={t('T20350')}
        ></span>
      </span>

      <div
        className="section_heading"
        style={{whiteSpace: 'pre-wrap'}}
      >
        <h4 className="section_heading_title">{t('T20351')}</h4>
      </div>
      <div className="img_box">
        <ImageSelector
          alt="driving insight data"
          kiaLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
        />
      </div>

      <ul className="application_list">
        {publicServiceContentsData.consents.map((consentItem) => {
          if (consentItem.category === CreatePublicServiceConsentRequest.category.VEHICLE_INSPECTION) {
            return (
              <VehicleInspectionContents
                puid={puid}
                vin={vin}
                key={consentItem.category}
                isAgree={consentItem.status}
              />
            );
          } else if (consentItem.category === CreatePublicServiceConsentRequest.category.DEMERIT_EDUCATION) {
            return (
              <DemeritEducationContents
                key={consentItem.category}
                vin={vin}
                isAgree={consentItem.status}
              />
            );
          } else if (consentItem.category === CreatePublicServiceConsentRequest.category.HIGHWAY_UNPAID) {
            return (
              <HighwayUnpaidContents
                key={consentItem.category}
                vin={vin}
                isAgree={consentItem.status}
              />
            );
          } else if (consentItem.category === CreatePublicServiceConsentRequest.category.LICENSE_RENEWAL) {
            return (
              <LicenseRenewalContents
                key={consentItem.category}
                vin={vin}
                isAgree={consentItem.status}
              />
            );
          }
        })}
      </ul>

      <button
        type="button"
        className="btn_service_management"
        onClick={() => goToPublicServiceTerms()}
      >
        {t('T20352')}
      </button>
    </section>
  );
}

function PublicServiceContentsLoading() {
  const {t} = useInferableTranslation();

  return (
    <section className="section section_application">
      <span className="logo">
        <span
          className="ic ic_digital_service"
          role="img"
          aria-label={t('T20350')}
        ></span>
      </span>

      <div
        className="section_heading"
        style={{whiteSpace: 'pre-wrap'}}
      >
        <h4 className="section_heading_title">{t('T20351')}</h4>
      </div>
      <div className="img_box">
        <ImageSelector
          alt="driving insight data"
          kiaLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/home/img_data_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/home/img_data.png')}
        />
      </div>
      <ul className="application_list">
        <li className="application_item asset_after asset_after">
          <div className="link">
            <div className="application_text">
              <strong className="title">::</strong>
              <p className="desc">::</p>
            </div>
            <span className="ic ic_arrow_forward_16px"></span>
          </div>
        </li>
        <li className="application_item asset_after skeleton">
          <div className="link">
            <div className="application_text">
              <strong className="title">::</strong>
              <p className="desc">::</p>
            </div>
            <span className="ic ic_arrow_forward_16px"></span>
          </div>
        </li>
        <li className="application_item asset_after skeleton">
          <div className="link">
            <div className="application_text">
              <strong className="title">::</strong>
              <p className="desc">::</p>
            </div>
            <span className="ic ic_arrow_forward_16px"></span>
          </div>
        </li>
        <li className="application_item asset_after skeleton">
          <div className="link">
            <div className="application_text">
              <strong className="title">::</strong>
              <p className="desc">::</p>
            </div>
            <span className="ic ic_arrow_forward_16px"></span>
          </div>
        </li>
      </ul>
    </section>
  );
}

export default PublicServiceContents;
